import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@lead-magnifier/ui'
import { Building2, Home, Check, ChevronDown } from 'lucide-react'
import { useWorkspace, type Workspace } from '@/lib/workspace-context'

export function WorkspaceSwitcher() {
  const { workspaces, activeWorkspace, setActiveWorkspace, isLoading } = useWorkspace()

  if (isLoading || !activeWorkspace) {
    return (
      <div className="flex h-16 items-center gap-2 border-b px-6">
        <div className="h-8 w-8 rounded-lg bg-muted animate-pulse" />
        <div className="h-4 w-24 rounded bg-muted animate-pulse" />
      </div>
    )
  }

  const personalWorkspaces = workspaces.filter((w) => w.type === 'personal')
  const orgWorkspaces = workspaces.filter((w) => w.type === 'organization')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex h-16 w-full items-center gap-2 border-b px-4 hover:bg-muted/50 transition-colors outline-none">
        <WorkspaceIcon workspace={activeWorkspace} />
        <div className="flex-1 text-left min-w-0">
          <p className="font-semibold text-sm truncate">{activeWorkspace.name}</p>
          <p className="text-xs text-muted-foreground">
            {activeWorkspace.type === 'personal' ? 'Persönlich' : 'Organisation'}
          </p>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-64" align="start" sideOffset={0}>
        {personalWorkspaces.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Persönlich
            </DropdownMenuLabel>
            {personalWorkspaces.map((workspace) => (
              <WorkspaceMenuItem
                key={workspace.id}
                workspace={workspace}
                isActive={workspace.id === activeWorkspace.id}
                onSelect={() => setActiveWorkspace(workspace)}
              />
            ))}
          </>
        )}

        {orgWorkspaces.length > 0 && (
          <>
            {personalWorkspaces.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Organisationen
            </DropdownMenuLabel>
            {orgWorkspaces.map((workspace) => (
              <WorkspaceMenuItem
                key={workspace.id}
                workspace={workspace}
                isActive={workspace.id === activeWorkspace.id}
                onSelect={() => setActiveWorkspace(workspace)}
              />
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function WorkspaceIcon({ workspace }: { workspace: Workspace }) {
  if (workspace.logoUrl) {
    return (
      <img
        src={workspace.logoUrl}
        alt={workspace.name}
        className="h-8 w-8 rounded-lg object-contain bg-muted"
      />
    )
  }

  const bgColor = workspace.primaryColor || (workspace.type === 'personal' ? '#3b82f6' : '#10b981')

  return (
    <div
      className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
      style={{ backgroundColor: bgColor }}
    >
      {workspace.type === 'personal' ? (
        <Home className="h-4 w-4" />
      ) : (
        <Building2 className="h-4 w-4" />
      )}
    </div>
  )
}

function WorkspaceMenuItem({
  workspace,
  isActive,
  onSelect,
}: {
  workspace: Workspace
  isActive: boolean
  onSelect: () => void
}) {
  return (
    <DropdownMenuItem
      onClick={onSelect}
      className="flex items-center gap-2 cursor-pointer"
    >
      <WorkspaceIcon workspace={workspace} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{workspace.name}</p>
        <p className="text-xs text-muted-foreground capitalize">
          {workspace.role === 'owner' ? 'Inhaber' : workspace.role === 'admin' ? 'Admin' : 'Aussteller'}
        </p>
      </div>
      {isActive && <Check className="h-4 w-4 text-primary shrink-0" />}
    </DropdownMenuItem>
  )
}
